import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, PROPERTY_STORAGE_KEY, getPropertyStorageKey } from '@/lib/api';
import {
  type PropertyModuleGrant,
  moduleAllowsRole,
} from '@/lib/modules';
import { invalidateAllQueries } from '@/lib/invalidate-queries';
import { useAuth } from '@/contexts/auth-context';
import { useMyWorkspaces } from '@/hooks/use-workspaces';
import {
  hasStaffHat as computeStaffHat,
  type ResidentMode,
  isDualHat,
  readStoredMode,
  writeStoredMode,
  hasResidentHat,
} from '@/lib/resident-mode';
import { type Workspace } from '@/types/superadmin';

const EMPTY_LIST: never[] = [];

export interface Property {
  id: string;
  name: string;
  address: string;
  city: string;
  postal_code: string;
  country?: string;
  /** Unit of account for every amount on this property. */
  currency?: string;
  /** BCP-47 formatting locale. Independent of `currency`. */
  locale?: string;
  /** True when posted/reconciled transactions block a currency change. */
  currency_locked?: boolean;
  /** Draft/void rows that a currency change would reinterpret (Q6). */
  currency_lock_draft_count?: number;
  property_type?: 'multi_unit' | 'single_home' | 'villa' | 'commercial' | string;
  total_floors: number;
  total_units?: number;
  maintenance_quote_auto_approve_threshold?: number | null;
  modules: PropertyModuleGrant[];
  status: 'active' | 'suspended' | 'archived' | 'pending_deletion' | string;
  workspace_id?: string;
  management_company?: string;
  management_company_logo?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  contact_notes?: string;
  manager_name?: string;
  manager_phone?: string;
  manager_email?: string;
  created_at: string;
  updated_at: string;
}

interface PropertyContextType {
  properties: Property[];
  allProperties: Property[];
  selectedPropertyId: string | null;
  selectedProperty: Property | null;
  setSelectedPropertyId: (id: string) => void;
  switchProperty: (id: string) => void;
  activeWorkspaceId: string | null;
  switchWorkspace: (id: string) => void;
  isLoading: boolean;
  refetchProperties: () => Promise<any>;
  hasModule: (id: string, role?: string) => boolean;
  isReadOnly: boolean;
  /** True when the active property list is the resident portfolio (all PMs). */
  isResidentScope: boolean;
  /** True when the account holds a staff/PM hat (any workspace, or a platform role). */
  hasStaffHat: boolean;
  residentMode: ResidentMode | null;
  setResidentMode: (mode: ResidentMode) => void;
  selectedUnitId: string | null;
  setSelectedUnitId: (id: string | null) => void;
  currentWorkspace: Workspace | null;
}

const PropertyContext = createContext<PropertyContextType | undefined>(undefined);

export const PropertyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { token, user, appMode } = useAuth();
  const { data: memberships = EMPTY_LIST, isLoading: membershipsLoading } = useMyWorkspaces();

  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState<string | null>(() => {
    return typeof window !== 'undefined' ? localStorage.getItem('koinon_active_workspace_id') : null;
  });

  useEffect(() => {
    if (memberships.length > 0) {
      const exists = memberships.some((m) => m.workspace_id === activeWorkspaceId);
      if (!activeWorkspaceId || !exists) {
        const defaultWsId = memberships[0].workspace_id;
        setActiveWorkspaceIdState(defaultWsId);
        localStorage.setItem('koinon_active_workspace_id', defaultWsId);
      }
    } else if (activeWorkspaceId) {
      setActiveWorkspaceIdState(null);
      localStorage.removeItem('koinon_active_workspace_id');
    }
  }, [memberships, activeWorkspaceId]);

  const { data: currentWorkspace = null } = useQuery<Workspace | null>({
    queryKey: ['current-workspace', activeWorkspaceId],
    queryFn: async () => {
      const res = await api.get('/workspaces/current');
      return res.data;
    },
    enabled: Boolean(token && activeWorkspaceId),
    staleTime: 1000 * 60 * 5,
  });

  const [selectedPropertyId, setSelectedPropertyIdState] = useState<string | null>(() => {
    const key = getPropertyStorageKey();
    let stored = localStorage.getItem(key);
    if (!stored && key !== PROPERTY_STORAGE_KEY) {
      const legacy = localStorage.getItem(PROPERTY_STORAGE_KEY);
      if (legacy) {
        localStorage.setItem(key, legacy);
        localStorage.removeItem(PROPERTY_STORAGE_KEY);
        stored = legacy;
      }
    }
    return stored;
  });

  const [selectedUnitId, setSelectedUnitIdState] = useState<string | null>(() => {
    return localStorage.getItem('koinon_selected_unit_id') || null;
  });

  const setSelectedUnitId = useCallback((id: string | null) => {
    setSelectedUnitIdState(id);
    if (id) {
      localStorage.setItem('koinon_selected_unit_id', id);
    } else {
      localStorage.removeItem('koinon_selected_unit_id');
    }
  }, []);

  const [residentMode, setResidentModeState] = useState<ResidentMode | null>(null);

  useEffect(() => {
    if (!user?.id || !isDualHat(user, memberships)) {
      setResidentModeState(null);
      return;
    }
    const stored = (appMode === 'manage' || appMode === 'home' ? appMode : null) ?? readStoredMode(user.id) ?? 'home';
    setResidentModeState((prev) => (prev === stored ? prev : stored));
  }, [user, memberships, appMode]);

  // The scope cannot be chosen until both hats are known. A resident hat alone
  // means the resident portfolio, but a dual-hat whose stored mode is 'manage'
  // must not briefly fetch resident scope first - so hold the query until the
  // workspaces query has settled AND the stored mode has been applied.
  const membershipsSettled = !membershipsLoading;
  const dualHat = isDualHat(user, memberships);
  // Only a dual-hat has a mode to resolve. Everyone else can be scoped as soon
  // as the workspaces query settles: a resident hat means the resident
  // portfolio, no hat at all means the managed list.
  const modeResolved = membershipsSettled && (!dualHat || residentMode !== null);
  const scope =
    residentMode === 'manage' ? 'manage' : hasResidentHat(user) ? 'resident' : 'manage';
  const isResidentScope = scope === 'resident';

  const hasStaffHat = useMemo(() => computeStaffHat(user, memberships), [user, memberships]);
  const {
    data: allProperties = EMPTY_LIST,
    isLoading,
    refetch: refetchProperties,
  } = useQuery<Property[]>({
    queryKey: ['properties', scope],
    queryFn: async () => {
      const res = await api.get(scope === 'resident' ? '/properties?scope=resident' : '/properties');
      return res.data;
    },
    enabled: Boolean(token) && modeResolved,
  });

  const properties = useMemo(() => {
    if (scope === 'resident' || !activeWorkspaceId) {
      return allProperties;
    }
    const hasWorkspaceIds = allProperties.some((p) => Boolean(p.workspace_id));
    if (!hasWorkspaceIds) {
      return allProperties;
    }
    return allProperties.filter((p) => p.workspace_id === activeWorkspaceId);
  }, [allProperties, scope, activeWorkspaceId]);

  useEffect(() => {
    if (!token || isLoading) {
      return;
    }
    if (properties.length > 0) {
      const currentValid = properties.find((p) => p.id === selectedPropertyId);
      if (!currentValid) {
        const firstActive = properties.find((p) => p.status !== 'pending_deletion');
        const firstId = (firstActive || properties[0]).id;
        setSelectedPropertyIdState(firstId);
        localStorage.setItem(getPropertyStorageKey(), firstId);
      }
    } else {
      setSelectedPropertyIdState(null);
      localStorage.removeItem(getPropertyStorageKey());
    }
  }, [properties, selectedPropertyId, isLoading, token]);

  const switchWorkspace = useCallback(
    (wsId: string) => {
      setActiveWorkspaceIdState(wsId);
      localStorage.setItem('koinon_active_workspace_id', wsId);

      const matchingProps = allProperties.filter((p) => p.workspace_id === wsId);
      if (matchingProps.length > 0) {
        const firstActive = matchingProps.find((p) => p.status !== 'pending_deletion');
        const firstId = (firstActive || matchingProps[0]).id;
        setSelectedPropertyIdState(firstId);
        localStorage.setItem(getPropertyStorageKey(), firstId);
      } else {
        setSelectedPropertyIdState(null);
        localStorage.removeItem(getPropertyStorageKey());
      }
      invalidateAllQueries(queryClient);
    },
    [allProperties, queryClient]
  );

  const switchProperty = useCallback(
    (id: string) => {
      const prop = allProperties.find((p) => p.id === id);
      if (prop?.workspace_id && prop.workspace_id !== activeWorkspaceId) {
        setActiveWorkspaceIdState(prop.workspace_id);
        localStorage.setItem('koinon_active_workspace_id', prop.workspace_id);
      }
      setSelectedPropertyIdState(id);
      localStorage.setItem(getPropertyStorageKey(), id);
      invalidateAllQueries(queryClient);
    },
    [allProperties, activeWorkspaceId, queryClient]
  );

  const setResidentMode = useCallback(
    (mode: ResidentMode) => {
      if (!user?.id) return;
      writeStoredMode(user.id, mode);
      setResidentModeState(mode);
      invalidateAllQueries(queryClient);
    },
    [queryClient, user]
  );

  const selectedProperty = useMemo(() => {
    return properties.find((p) => p.id === selectedPropertyId) || null;
  }, [properties, selectedPropertyId]);

  const moduleGrants = useMemo(() => {
    return selectedProperty?.modules ?? [];
  }, [selectedProperty]);

  const hasModule = useCallback(
    (id: string, role?: string): boolean => {
      const grant = moduleGrants.find((g) => g.module_id === id);
      if (!grant) return false;
      if (role) {
        return moduleAllowsRole(grant, role);
      }
      return true;
    },
    [moduleGrants]
  );

  const isReadOnly = useMemo(() => {
    if (user && user.role === 'superadmin') {
      return false;
    }
    if (selectedProperty) {
      return selectedProperty.status === 'suspended' || selectedProperty.status === 'pending_deletion';
    }
    if (memberships.length > 0) {
      const active =
        memberships.find((m) => m.workspace_id === activeWorkspaceId) || memberships[0];
      if (active) {
        return active.workspace_status === 'suspended' || active.workspace_status === 'pending_deletion';
      }
    }
    return properties.some((p) => p.status === 'suspended' || p.status === 'pending_deletion');
  }, [user, memberships, properties, selectedProperty, activeWorkspaceId]);

  useEffect(() => {
    const onReadOnly403 = () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    };
    window.addEventListener('koinon:read-only-mode', onReadOnly403);
    return () => window.removeEventListener('koinon:read-only-mode', onReadOnly403);
  }, [queryClient]);

  const contextValue: PropertyContextType = useMemo(
    () => ({
      properties,
      allProperties,
      selectedPropertyId,
      selectedProperty,
      setSelectedPropertyId: switchProperty,
      switchProperty,
      activeWorkspaceId,
      switchWorkspace,
      isLoading,
      refetchProperties,
      hasModule,
      isReadOnly,
      isResidentScope,
      hasStaffHat,
      residentMode,
      setResidentMode,
      selectedUnitId,
      setSelectedUnitId,
      currentWorkspace,
    }),
    [
      properties,
      allProperties,
      selectedPropertyId,
      selectedProperty,
      switchProperty,
      activeWorkspaceId,
      switchWorkspace,
      isLoading,
      refetchProperties,
      hasModule,
      isReadOnly,
      isResidentScope,
      hasStaffHat,
      residentMode,
      setResidentMode,
      selectedUnitId,
      setSelectedUnitId,
      currentWorkspace,
    ]
  );

  return (
    <PropertyContext.Provider value={contextValue}>{children}</PropertyContext.Provider>
  );
};

  // eslint-disable-next-line react-refresh/only-export-components
export const useProperty = (): PropertyContextType => {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('useProperty must be used within a PropertyProvider');
  }
  return context;
};
